import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { IconDownload, IconPlus, IconSearch, IconTasks } from '../components/icons';
import { Avatar, Badge, Card, EmptyState, Loading, ModuleHeader, SearchSelect, useAsync, useToast } from '../components/ui';
import { api, downloadFile } from '../lib/api';
import { useAuth } from '../lib/auth';
import {
  formatDateTime,
  formatRelative,
  SLA_LABELS,
  SLA_VARIANTS,
  STATUS_VARIANTS,
} from '../lib/format';

export function TasksPage() {
  const navigate = useNavigate();
  const { can } = useAuth();
  const { notify } = useToast();
  const [params, setParams] = useSearchParams();

  const [search, setSearch] = useState(params.get('search') ?? '');
  const filters = useMemo(
    () => ({
      search: params.get('search') ?? '',
      status: params.get('status') ?? '',
      open: params.get('open') ?? '',
      mine: params.get('mine') ?? '',
      overdue: params.get('overdue') ?? '',
      dueToday: params.get('dueToday') ?? '',
      emergency: params.get('emergency') ?? '',
      propertyId: params.get('propertyId') ?? '',
      criticalityId: params.get('criticalityId') ?? '',
      categoryId: params.get('categoryId') ?? '',
      assigneeId: params.get('assigneeId') ?? '',
      sort: params.get('sort') ?? 'due',
    }),
    [params],
  );

  const catalogs = useAsync(() => api.get('/api/catalogs'), []);
  const meta = useAsync(() => api.get('/api/tasks/meta'), []);
  const properties = useAsync(() => api.get('/api/properties'), []);
  const tasks = useAsync(() => api.get('/api/tasks', { ...filters, limit: 200 }), [params.toString()]);

  const setFilter = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const activeFilters = Object.entries(filters).filter(([key, value]) => value && key !== 'sort');

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconTasks width={22} height={22} />}
        title="Aufträge"
        subtitle={
          <>
            Zentrale Liste aller Meldungen und Aufträge – unabhängig vom Eingangskanal. Angezeigt werden
            ausschließlich Vorgänge in Ihrem Zugriffsbereich.
          </>
        }
        pill="Meldungseingang"
        tone="primary"
        actions={
          <>
            {can('tasks.export') && (
              <button
                className="btn"
                onClick={() =>
                  downloadFile('/api/tasks/export.csv', 'aufgaben.csv', filters).catch(() =>
                    notify('Export fehlgeschlagen.', 'error'),
                  )
                }
              >
                <IconDownload width={15} height={15} /> Export
              </button>
            )}
            {can('tasks.create') && (
              <button className="btn primary" onClick={() => navigate('/tasks/new')}>
                <IconPlus width={15} height={15} /> Neue Meldung
              </button>
            )}
          </>
        }
      />

      <Card noBody>
        <div className="card-body tight">
          <div className="row" style={{ gap: 10 }}>
            <div style={{ position: 'relative', flex: '1 1 260px', minWidth: 200 }}>
              <IconSearch
                width={15}
                height={15}
                style={{ position: 'absolute', left: 10, top: 10, color: 'var(--text-subtle)' }}
              />
              <input
                type="search"
                placeholder="Titel, Beschreibung oder Nummer suchen …"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => event.key === 'Enter' && setFilter('search', search)}
                style={{ paddingLeft: 32 }}
              />
            </div>

            <SearchSelect
              value={String(filters.status ?? '')}
              onChange={(wert) => setFilter('status', wert)}
              options={(meta.data?.statuses ?? []).map((status: any) => ({ value: String(status.key), label: String(status.label) }))}
              placeholder="Alle Status"
              style={{ width: 190 }}
            />

            <SearchSelect
              value={String(filters.criticalityId ?? '')}
              onChange={(wert) => setFilter('criticalityId', wert)}
              options={(catalogs.data?.criticalityLevels ?? []).map((level: any) => ({ value: String(level.id), label: String(level.name) }))}
              placeholder="Alle Kritikalitäten"
              style={{ width: 170 }}
            />

            <SearchSelect
              value={String(filters.propertyId ?? '')}
              onChange={(wert) => setFilter('propertyId', wert)}
              options={(properties.data ?? []).map((property: any) => ({ value: String(property.id), label: String(property.name) }))}
              placeholder="Alle Objekte"
              style={{ width: 200 }}
            />

            <div className="btn-group">
              <button className={`btn sm ${filters.open === 'true' ? 'active' : ''}`} onClick={() => setFilter('open', filters.open ? '' : 'true')}>
                Offen
              </button>
              <button className={`btn sm ${filters.mine === 'true' ? 'active' : ''}`} onClick={() => setFilter('mine', filters.mine ? '' : 'true')}>
                Meine
              </button>
              <button
                className={`btn sm ${filters.emergency === 'true' ? 'active' : ''}`}
                onClick={() => setFilter('emergency', filters.emergency ? '' : 'true')}
                title="Nur Vorgänge der höchsten Kritikalitätsstufe"
              >
                Nur Notfälle
              </button>
              <button
                className={`btn sm ${filters.dueToday === 'true' ? 'active' : ''}`}
                onClick={() => setFilter('dueToday', filters.dueToday ? '' : 'true')}
                title="Frist endet im Lauf des Tages"
              >
                Heute fällig
              </button>
              <button className={`btn sm ${filters.overdue === 'true' ? 'active' : ''}`} onClick={() => setFilter('overdue', filters.overdue ? '' : 'true')}>
                Überfällig
              </button>
            </div>

            <select value={filters.sort} onChange={(event) => setFilter('sort', event.target.value)} style={{ width: 165 }}>
              <option value="due">Nach Fälligkeit</option>
              <option value="criticality">Nach Kritikalität</option>
              <option value="created">Nach Eingang</option>
              <option value="status">Nach Status</option>
            </select>
          </div>

          {activeFilters.length > 0 && (
            <div className="row" style={{ marginTop: 10, gap: 6 }}>
              {activeFilters.map(([key, value]) => (
                <span className="chip" key={key}>
                  {filterLabel(key)}
                  <button
                    onClick={() => {
                      if (key === 'search') setSearch('');
                      setFilter(key, '');
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
              <button
                className="btn ghost sm"
                onClick={() => {
                  setSearch('');
                  setParams(new URLSearchParams(), { replace: true });
                }}
              >
                Alle zurücksetzen
              </button>
            </div>
          )}
        </div>

        {tasks.loading ? (
          <Loading />
        ) : !tasks.data?.items?.length ? (
          <EmptyState
            title="Keine Aufträge gefunden"
            hint="Passen Sie die Filter an oder legen Sie eine neue Meldung an."
          />
        ) : (
          <div className="table-wrap">
            <table className="data">
              <thead>
                <tr>
                  <th style={{ width: 42 }}>SLA</th>
                  <th>Nummer</th>
                  <th>Titel</th>
                  <th>Objekt</th>
                  <th>Kritikalität</th>
                  <th>Status</th>
                  <th>Zuständig</th>
                  <th>Fällig</th>
                </tr>
              </thead>
              <tbody>
                {tasks.data.items.map((task: any) => (
                  <tr key={task.id} className="clickable" onClick={() => navigate(`/tasks/${task.id}`)}>
                    <td>
                      <span
                        className={`sla-dot sla-${task.slaState}`}
                        title={SLA_LABELS[task.slaState] ?? task.slaState}
                      />
                    </td>
                    <td className="mono nowrap">{task.task_no}</td>
                    <td>
                      <div className="truncate" style={{ maxWidth: 320, fontWeight: 550 }}>
                        {task.title}
                      </div>
                      {task.location_detail && (
                        <div className="small subtle truncate" style={{ maxWidth: 320 }}>
                          {task.location_detail}
                        </div>
                      )}
                    </td>
                    <td className="small">{task.property_name ?? '–'}</td>
                    <td>
                      <Badge color={task.criticality_color}>{task.criticality_name ?? '–'}</Badge>
                    </td>
                    <td>
                      <Badge variant={STATUS_VARIANTS[task.status] ?? ''}>{task.statusLabel}</Badge>
                    </td>
                    <td>
                      {task.assignee_first_name ? (
                        <div className="row" style={{ gap: 7, flexWrap: 'nowrap' }}>
                          <Avatar firstName={task.assignee_first_name} lastName={task.assignee_last_name} size="sm" />
                          <span className="small nowrap">
                            {task.assignee_first_name} {task.assignee_last_name}
                          </span>
                        </div>
                      ) : (
                        <span className="small subtle">nicht zugewiesen</span>
                      )}
                    </td>
                    <td className="small nowrap" title={formatDateTime(task.due_at)}>
                      {task.due_at ? (
                        <span className={task.slaState === 'overdue' ? 'badge danger' : ''}>
                          {formatRelative(task.due_at)}
                        </span>
                      ) : (
                        '–'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tasks.data && (
          <div className="card-body tight row between small subtle">
            <span>
              {tasks.data.items.length} von {tasks.data.total} Aufträge
            </span>
            <span>Legende: SLA-Ampel grün = im Plan, gelb = Frist läuft ab, rot = überfällig</span>
          </div>
        )}
      </Card>
    </div>
  );
}

function filterLabel(key: string): string {
  return (
    {
      search: 'Suche',
      status: 'Status',
      open: 'Nur offene',
      mine: 'Meine Aufträge',
      overdue: 'Überfällig',
      dueToday: 'Heute fällig',
      emergency: 'Notfall',
      propertyId: 'Objekt',
      criticalityId: 'Kritikalität',
      categoryId: 'Kategorie',
      assigneeId: 'Zuständig',
    }[key] ?? key
  );
}

export { SLA_VARIANTS };
